import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faChevronDown, faChevronLeft, faGear, faKey, faSave } from '@fortawesome/pro-solid-svg-icons';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { InputSwitchModule } from 'primeng/inputswitch';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ToastModule } from 'primeng/toast';
import { MenuModule } from 'primeng/menu';
import { MessageService } from 'primeng/api';
import { UsersService } from '../../../core/services/users/users.service';
import { AuthService } from '../../../core/services/auth.service';
import { PermissionService } from '../../../core/services/permission.service';
import { DEPARTMENT_LABELS, ROL_LABELS } from '../../../core/constants/user.constants';
import { Rol } from '../../../core/enums/rol.enum';
import { Permission } from '../../../core/enums/permission.enum';
import { UserDeactivationBlocker } from '../../../core/models/user-deactivation-blocker.model';
import { UserDeactivationBlockedModalComponent } from '../../../components/user-deactivation-blocked-modal/user-deactivation-blocked-modal.component';

@Component({
  selector: 'app-user-detail',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    FontAwesomeModule,
    DropdownModule,
    InputTextModule,
    InputSwitchModule,
    ProgressSpinnerModule,
    ToastModule,
    MenuModule,
    UserDeactivationBlockedModalComponent
  ],
  providers: [MessageService],
  templateUrl: './user-detail.component.html',
  styleUrl: './user-detail.component.scss'
})
export class UserDetailComponent implements OnInit, OnDestroy {
  
  faChevronLeft = faChevronLeft;
  faSave = faSave;
  faGear = faGear;
  faChevronDown = faChevronDown;
  faKey = faKey;

  userForm: FormGroup;
  userId: number | null = null;
  isLoading = false;
  isSaving = false;
  isNewUser = false;
  canManageUsers = false;
  isAdmin = false;
  isOwnProfile = false;
  user: any = null;

  departments = DEPARTMENT_LABELS;
  allRoles = ROL_LABELS;
  selectedRoles: Rol[] = [];

  actionMenuItems: any[] = [];

  isPasswordModalVisible = false;
  newPassword = '';
  confirmPassword = '';
  passwordError = '';
  isSavingPassword = false;

  previousIsActive = true;
  isDeactivationBlockedModalVisible = false;
  blockedClients: UserDeactivationBlocker[] = [];
  isCheckingDeactivation = false;

  private listPage = 0;
  private listName: string | undefined;
  private listDepartment: string | undefined;
  private listRol: string | undefined;
  private listStatus: boolean | undefined;

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private usersService: UsersService,
    private authService: AuthService,
    private permissionService: PermissionService,
    private messageService: MessageService
  ) {
    this.userForm = this.fb.group({
      id: [null],
      name: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      department: [null, Validators.required],
      isActive: [true],
      password: ['']
    });
  }

  async ngOnInit() {
    this.canManageUsers = this.permissionService.hasPermission(Permission.ManageUsers);
    this.isAdmin = this.permissionService.hasRole(Rol.Admin);

    this.route.paramMap.subscribe(async params => {
      this.isLoading = true;
      const qp = this.route.snapshot.queryParams;
      this.listPage = Number.isInteger(Number(qp['page'])) && Number(qp['page']) > 0 ? Number(qp['page']) : 0;
      this.listName = qp['name'] || undefined;
      this.listDepartment = qp['department'] || undefined;
      this.listRol = qp['rol'] || undefined;
      this.listStatus = qp['isActive'] !== undefined ? qp['isActive'] === 'true' : undefined;

      const id = params.get('id');
      
      if (id === 'new') {
        this.isNewUser = true;
        if (!this.canManageUsers) {
          this.navigateToList();
          return;
        }
        this.userForm.patchValue({ isActive: true });
        this.selectedRoles = [Rol.Office];
        this.userForm.get('password')?.setValidators([Validators.required, Validators.minLength(6)]);
        this.userForm.get('password')?.updateValueAndValidity();
      } else {
        this.userId = Number(id);
        this.isNewUser = false;
        const loggedUserId = this.authService.getUserId();
        this.isOwnProfile = loggedUserId !== null && this.userId === loggedUserId;
        await this.loadUser();
        this.userForm.get('password')?.clearValidators();
        this.userForm.get('password')?.updateValueAndValidity();
        this.buildActionMenu();
      }

      // Solo un Admin puede modificar a otro Admin (lo exige el backend): se muestra en solo lectura.
      this.isReadOnlyAdmin = !this.isAdmin && Array.isArray(this.user?.roles) && this.user.roles.includes(Rol.Admin);
      if (this.isReadOnlyAdmin) {
        this.userForm.disable();
      } else if (!this.canManageUsers) {
        this.userForm.get('isActive')?.disable();
        this.userForm.get('email')?.disable();
        this.userForm.get('department')?.disable();
      } else if (this.isOwnProfile) {
        this.userForm.get('isActive')?.disable(); // desactivarse a uno mismo lo dejaría sin acceso
      }

      this.isLoading = false;
    });
  }

  buildActionMenu() {
    this.actionMenuItems = [
      {
        label: 'Modificar contraseña',
        icon: this.faKey,
        command: () => this.openPasswordModal()
      }
    ];
  }

  async loadUser() {
    try {
      this.user = await this.usersService.getUserById(this.userId!);
      this.userForm.patchValue({
        id: this.user.id,
        name: this.user.name,
        email: this.user.email,
        department: this.user.department,
        isActive: this.user.isActive
      });
      this.selectedRoles = Array.isArray(this.user.roles) ? [...this.user.roles] : [];
      this.previousIsActive = this.user.isActive;
    } catch (error) {
      console.error('Error loading user:', error);
      this.navigateToList();
    }
  }

  private navigateToList() {
    const queryParams: Record<string, string | number | boolean | null> = {
      page: this.listPage ?? 0,
      name: this.listName ?? null,
      department: this.listDepartment ?? null,
      rol: this.listRol ?? null,
      isActive: this.listStatus !== undefined ? this.listStatus : null
    };
    this.router.navigate(['/users'], { queryParams });
  }

  get initials(): string {
    return (this.user?.name ?? '').trim().split(/\s+/).slice(0, 3).map((w: string) => w[0]?.toUpperCase() ?? '').join('') || '?';
  }

  /** Hay algún rol que el usuario no puede quitarse a sí mismo (para mostrar el motivo). */
  get hasLockedRoles(): boolean {
    return this.isRoleLocked(Rol.Admin) || this.isRoleLocked(Rol.UsersManager);
  }

  isRoleSelected(role: Rol): boolean {
    return this.selectedRoles.includes(role);
  }

  /** Nadie puede quitarse a sí mismo los roles que dan acceso a la gestión de usuarios. */
  isRoleLocked(role: Rol): boolean {
    return this.isOwnProfile && this.isRoleSelected(role) && (role === Rol.Admin || role === Rol.UsersManager);
  }

  toggleRole(role: Rol) {
    if (!this.canManageUsers || this.isReadOnlyAdmin) return;
    if (!this.isAdmin && role === Rol.Admin) return;
    if (this.isRoleLocked(role)) return;

    const idx = this.selectedRoles.indexOf(role);
    if (idx >= 0) {
      this.selectedRoles = this.selectedRoles.filter(r => r !== role);
    } else {
      this.selectedRoles = [...this.selectedRoles, role];
    }
  }

  async onActiveStatusChange(event: { checked: boolean }) {
    if (!this.canManageUsers || this.isNewUser || !this.userId || event.checked) {
      return;
    }

    const blocked = await this.checkDeactivationBlockers();
    if (blocked) {
      this.userForm.patchValue({ isActive: true }, { emitEvent: false });
    }
  }

  async checkDeactivationBlockers(): Promise<boolean> {
    if (!this.userId || this.isCheckingDeactivation) {
      return false;
    }

    this.isCheckingDeactivation = true;
    try {
      const result = await this.usersService.getDeactivationBlockers(this.userId);
      if (!result.canDeactivate) {
        this.blockedClients = result.clients;
        this.isDeactivationBlockedModalVisible = true;
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error checking deactivation blockers:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No se pudo comprobar si el usuario puede desactivarse'
      });
      return true;
    } finally {
      this.isCheckingDeactivation = false;
    }
  }

  closeDeactivationBlockedModal() {
    this.isDeactivationBlockedModalVisible = false;
    this.blockedClients = [];
  }

  private savedAndLeaving = false;
  private leaveTimer?: ReturnType<typeof setTimeout>;
  /** Un gestor de usuarios que abre a un Admin no puede editarlo. */
  isReadOnlyAdmin = false;

  async save() {
    if (this.isSaving || this.isReadOnlyAdmin) return;
    if (this.userForm.invalid) {
      Object.keys(this.userForm.controls).forEach(key => {
        this.userForm.get(key)?.markAsTouched();
      });
      return;
    }

    if (this.selectedRoles.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Atención',
        detail: 'El usuario debe tener al menos un rol asignado'
      });
      return;
    }

    this.isSaving = true;
    try {
      const userData = this.userForm.getRawValue();
      userData.roles = this.selectedRoles;

      if (!this.isNewUser) {
        delete userData.password;

        if (userData.isActive === false && this.previousIsActive) {
          const blocked = await this.checkDeactivationBlockers();
          if (blocked) {
            this.userForm.patchValue({ isActive: true }, { emitEvent: false });
            return;
          }
        }
      }

      await this.usersService.createOrUpdate(userData);
      
      this.messageService.add({
        severity: 'success',
        summary: 'Éxito',
        detail: this.isNewUser ? 'Usuario creado correctamente' : 'Usuario actualizado correctamente'
      });
      
      // Se vuelve a la lista: hasta entonces el botón sigue bloqueado (un segundo clic duplicaría el alta).
      this.savedAndLeaving = true;
      this.leaveTimer = setTimeout(() => this.goBack(), 1500);
    } catch (error: any) {
      if (error?.status === 409 && error?.error?.clients?.length) {
        this.blockedClients = error.error.clients;
        this.isDeactivationBlockedModalVisible = true;
        this.userForm.patchValue({ isActive: true }, { emitEvent: false });
        return;
      }

      console.error('Error saving user:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: error?.error?.message || 'Error al guardar el usuario'
      });
    } finally {
      if (!this.savedAndLeaving) {
        this.isSaving = false;
      }
    }
  }

  openPasswordModal() {
    this.newPassword = '';
    this.confirmPassword = '';
    this.passwordError = '';
    this.isPasswordModalVisible = true;
  }

  closePasswordModal() {
    this.isPasswordModalVisible = false;
    this.newPassword = '';
    this.confirmPassword = '';
    this.passwordError = '';
  }

  async savePassword() {
    this.passwordError = '';

    if (!this.newPassword || !this.confirmPassword) {
      this.passwordError = 'Ambos campos son obligatorios';
      return;
    }

    if (this.newPassword.length < 6) {
      this.passwordError = 'La contraseña debe tener al menos 6 caracteres';
      return;
    }

    if (this.newPassword !== this.confirmPassword) {
      this.passwordError = 'Las contraseñas no coinciden';
      return;
    }

    this.isSavingPassword = true;
    try {
      await this.usersService.changePassword(this.userId!, this.newPassword, this.confirmPassword);
      this.closePasswordModal();
      this.messageService.add({
        severity: 'success',
        summary: 'Éxito',
        detail: 'Contraseña actualizada correctamente'
      });
    } catch (error) {
      console.error('Error changing password:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Error al actualizar la contraseña'
      });
    } finally {
      this.isSavingPassword = false;
    }
  }

  goBack() {
    if (this.isOwnProfile && !this.canManageUsers) {
      this.router.navigate(['/home']);
      return;
    }
    this.navigateToList();
  }

  @HostListener('document:keydown.escape')
  onEscape() {
    if (this.isPasswordModalVisible) this.closePasswordModal();
  }

  ngOnDestroy() {
    clearTimeout(this.leaveTimer); // si el usuario ya se ha ido, no redirigirlo después
  }
}
